import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import Modal from "@/components/app/Modal";
import { INDIA_STATES, matchState } from "@/lib/india";
import { personErrors } from "@/lib/personValidation";
import { btnOutline, btnPrimary, inputCls, inputErrCls, labelCls } from "@/components/app/ui";

const LABELS = ["Home", "Work", "Other"];
const blankAddr = { label: "Home", address: "", city: "", state: "", pincode: "" };

/** Add a delivery address to a customer: pincode first, it fills city and state (like the storefront checkout). */
export function AddressModal({ open, customer, onClose, onSaved }) {
  const [f, setF] = useState(blankAddr);
  const [errs, setErrs] = useState({});
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setF(blankAddr); setErrs({}); setNote(""); } }, [open]);
  const set = (k, v) => { setF((x) => ({ ...x, [k]: v })); setErrs((e) => ({ ...e, [k]: undefined })); };
  const pin = f.pincode.trim();

  useEffect(() => {
    if (!open || !/^[1-9]\d{5}$/.test(pin)) { setNote(""); return undefined; }
    let live = true;
    setNote("Finding city and state…");
    api.get(`/pincode/${pin}`).then(({ data }) => {
      if (!live) return;
      const state = matchState(data.state);
      setF((x) => (x.pincode.trim() === pin ? { ...x, city: data.city || x.city, state: state || x.state } : x));
      setErrs((e) => ({ ...e, city: data.city ? undefined : e.city, state: state ? undefined : e.state }));
      setNote(data.city ? `${data.city}, ${data.state}` : "");
    }).catch((err) => { if (live) setNote(err.response?.status === 404 ? "Pincode not found — fill in the city and state yourself." : ""); });
    return () => { live = false; };
  }, [open, pin]);

  const save = async () => {
    const all = personErrors({ name: "Valid Name", mobile: "9999999999", ...f }, { delivery: true });
    const e = Object.fromEntries(["address", "city", "state", "pincode"].filter((k) => all[k]).map((k) => [k, all[k]]));
    setErrs(e);
    if (Object.keys(e).length) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/vendor/customers/${customer.id}/addresses`, { label: f.label, address: f.address.trim(), city: f.city.trim(), state: f.state.trim(), pincode: pin, is_default: false });
      toast.success("Address added.");
      const added = data.addresses[data.addresses.length - 1];
      onSaved(data.addresses, added);
      onClose();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const cls = (k) => `${inputCls} ${errs[k] ? inputErrCls : ""}`;
  const Err = ({ k }) => (errs[k] ? <p role="alert" className="mt-1 text-[11px] text-red-600">{errs[k]}</p> : null);
  return (
    <Modal open={open} onClose={onClose} size="md" title="Add address" subtitle={customer ? `For ${customer.name}` : ""} testId="pos-address-modal"
      footer={<><button onClick={onClose} className={btnOutline}>Cancel</button><button onClick={save} disabled={saving} data-testid="pos-address-save" className={btnPrimary}>{saving ? "Saving…" : "Save address"}</button></>}>
      <div className="space-y-3 text-[13px]">
        <div>
          <label className={labelCls}>Address type</label>
          <div role="radiogroup" className="flex gap-2" data-testid="pos-address-type">
            {LABELS.map((l) => (
              <label key={l} className={`inline-flex items-center gap-2 px-3.5 h-9 rounded-full border text-[12px] font-semibold cursor-pointer ${f.label === l ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-300 text-slate-600"}`}>
                <input type="radio" name="pos-addr-type" checked={f.label === l} onChange={() => set("label", l)} data-testid={`pos-address-type-${l.toLowerCase()}`} />{l}
              </label>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div><label className={labelCls}>Pincode *</label><input value={f.pincode} maxLength={6} inputMode="numeric" placeholder="6-digit" data-testid="pos-address-pincode"
            onChange={(e) => set("pincode", e.target.value.replace(/\D/g, ""))} className={cls("pincode")} /><Err k="pincode" />{!errs.pincode && note && <p className="mt-1 text-[11px] text-slate-500">{note}</p>}</div>
          <div><label className={labelCls}>City *</label><input value={f.city} maxLength={60} onChange={(e) => set("city", e.target.value)} data-testid="pos-address-city" className={cls("city")} /><Err k="city" /></div>
          <div><label className={labelCls}>State *</label>
            <select value={f.state} onChange={(e) => set("state", e.target.value)} data-testid="pos-address-state" className={cls("state")}>
              <option value="">Select</option>{INDIA_STATES.map((st) => <option key={st} value={st}>{st}</option>)}
            </select><Err k="state" /></div>
        </div>
        <div><label className={labelCls}>Address *</label><textarea rows={2} maxLength={300} value={f.address} placeholder="House no., street, area, landmark" data-testid="pos-address-line"
          onChange={(e) => set("address", e.target.value)} className={`${cls("address")} !h-auto py-2`} /><Err k="address" /></div>
      </div>
    </Modal>
  );
}

/** Add a delivery time slot (shown in the POS "Time slot" list). */
export function TimeSlotModal({ open, onClose, onSaved }) {
  const [start, setStart] = useState("10:00");
  const [end, setEnd] = useState("12:00");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setStart("10:00"); setEnd("12:00"); setErr(""); } }, [open]);
  const save = async () => {
    if (!start || !end) return setErr("Enter the start and end time.");
    if (end <= start) return setErr("The end time must be after the start time.");
    setSaving(true); setErr("");
    try { const { data } = await api.post("/vendor/time-slots", { start, end }); toast.success(`Time slot ${data.label} added.`); onSaved(data); onClose(); }
    catch (e) { setErr(apiErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Modal open={open} onClose={onClose} size="md" title="Add time slot" subtitle="Delivery window customers can be given, e.g. 10:00 AM – 12:00 PM" testId="pos-slot-modal"
      footer={<><button onClick={onClose} className={btnOutline}>Cancel</button><button onClick={save} disabled={saving} data-testid="pos-slot-save" className={btnPrimary}>{saving ? "Saving…" : "Save time slot"}</button></>}>
      <div className="grid grid-cols-2 gap-3 text-[13px]">
        <div><label className={labelCls}>From</label><input type="time" value={start} onChange={(e) => { setStart(e.target.value); setErr(""); }} data-testid="pos-slot-start" className={inputCls} /></div>
        <div><label className={labelCls}>To</label><input type="time" value={end} onChange={(e) => { setEnd(e.target.value); setErr(""); }} data-testid="pos-slot-end" className={inputCls} /></div>
      </div>
      {err && <p role="alert" data-testid="pos-slot-error" className="mt-3 text-[12px] text-red-600">{err}</p>}
    </Modal>
  );
}


/** Pick one of the store's coupons for this order. Each shows a Valid / Invalid badge from its conditions (dates, minimum order, uses left, per-customer limit). */
export function DiscountModal({ open, subtotal, customerId, applied, onClose, onPick }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    if (!open) return;
    setRows(null); setErr("");
    api.get("/vendor/pos/coupons", { params: { subtotal, customer_id: customerId } }).then(({ data }) => setRows(data)).catch((e) => setErr(apiErr(e.response?.data?.detail)));
  }, [open, subtotal, customerId]);
  const terms = (c) => `${c.discount_type === "percent" ? `${c.value}% off` : `₹${c.value} off`}${c.min_order ? ` · min order ₹${c.min_order}` : ""}`;
  return (
    <Modal open={open} onClose={onClose} size="md" title="Apply a coupon" subtitle={`Order subtotal ₹${Number(subtotal || 0).toLocaleString("en-IN")}`} testId="pos-discount-modal"
      footer={<button onClick={onClose} className={btnOutline}>Close</button>}>
      {err && <p role="alert" className="text-[12px] text-red-600">{err}</p>}
      {!rows && !err && <p className="text-[13px] text-slate-500 py-6 text-center">Loading coupons…</p>}
      {rows && rows.length === 0 && <p className="text-[13px] text-slate-500 py-6 text-center" data-testid="pos-coupons-empty">This store has no coupons yet. Create one on the Coupons page.</p>}
      <ul className="space-y-2" data-testid="pos-coupon-list">
        {rows?.map((c) => (
          <li key={c.code}>
            <button type="button" disabled={!c.valid} onClick={() => onPick(c)} data-testid={`pos-coupon-${c.code}`} aria-label={`${c.code} ${c.valid ? "valid" : "invalid"}`}
              className={`w-full text-left rounded-lg border px-3 py-2.5 flex items-start justify-between gap-3 transition ${c.valid ? "border-slate-200 hover:border-green-400 hover:bg-green-50/40" : "border-slate-200 bg-slate-50 opacity-80 cursor-not-allowed"} ${applied === c.code ? "ring-2 ring-green-400" : ""}`}>
              <span className="min-w-0">
                <span className="block font-mono font-bold text-[13px] text-neutral-900">{c.code}{applied === c.code && <span className="ml-2 text-[10px] font-sans text-green-700">Applied</span>}</span>
                <span className="block text-[12px] text-slate-600">{terms(c)}</span>
                {c.valid ? <span className="block text-[11px] text-green-700 mt-0.5">Saves ₹{Number(c.discount).toLocaleString("en-IN")} on this order</span>
                  : <span className="block text-[11px] text-red-600 mt-0.5" data-testid={`pos-coupon-reason-${c.code}`}>{c.reason}</span>}
              </span>
              <span className={`shrink-0 text-[11px] font-bold px-2 py-0.5 rounded-md border ${c.valid ? "bg-green-50 text-green-700 border-green-200" : "bg-red-50 text-red-700 border-red-200"}`}>{c.valid ? "Valid" : "Invalid"}</span>
            </button>
          </li>
        ))}
      </ul>
    </Modal>
  );
}
